// ============================================================================
// SYUT EMPLOYEE SELECT
// ============================================================================
// Domain wrapper around SyutSelect pre-configured to search the Employee master.
// Accepts a `role` prop to filter to managers, partners, or all employees.
// Scales to any number of employees (server-side searchTerm).

import React from 'react';
import { SyutSelect } from '../SyutSelect';
import { employeeService } from 'api/services/employeeService';

export type EmployeeRole = 'manager' | 'partner' | 'any';

export interface SyutEmployeeSelectProps {
  value?: string;
  onChange: (value: string) => void;
  label?: React.ReactNode;
  error?: string;
  required?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  /** 'manager' → isManager=true, 'partner' → isPartner=true, 'any' (default) → no role filter */
  role?: EmployeeRole;
  seedOptions?: { value: string; label: string }[];
  includeInactive?: boolean;
  includeLeft?: boolean;
  /** Rows per server query (default: 500). */
  pageSize?: number;
  disabled?: boolean;
}

export const SyutEmployeeSelect: React.FC<SyutEmployeeSelectProps> = ({
  value,
  onChange,
  label,
  error,
  required,
  isClearable = true,
  placeholder,
  size = 'sm',
  role = 'any',
  seedOptions,
  includeInactive = false,
  includeLeft = false,
  pageSize = 500,
  disabled,
}) => {
  const defaultPlaceholder =
    placeholder ??
    (role === 'manager'
      ? 'Type 3 letters to search managers...'
      : role === 'partner'
      ? 'Type 3 letters to search partners...'
      : 'Type 3 letters to search employees...');

  return (
    <SyutSelect
      label={label}
      value={value}
      isAsync
      loadOptions={async (input: string) => {
        const res = await employeeService.getAll({
          page: 1,
          pageSize,
          searchTerm: input || undefined,
          isManager: role === 'manager' ? true : undefined,
          isPartner: role === 'partner' ? true : undefined,
          includeInactive,
          includeLeft,
        });
        return res.data.map((e: any) => ({
          value: e.id,
          label: `${e.name || ''}${e.code ? ` (${e.code})` : ''}`.trim(),
        }));
      }}
      defaultOptions
      cacheOptions={false}
      seedOptions={seedOptions}
      debounceDelay={300}
      minSearchLength={3}
      isClearable={isClearable}
      size={size}
      placeholder={defaultPlaceholder}
      error={error}
      required={required}
      isDisabled={disabled}
      onChange={(val) => onChange((val as string) || '')}
    />
  );
};

export default SyutEmployeeSelect;
